import { useTranslation } from 'react-i18next';

import { Accordion } from '../../components/Accordion';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import type { ArticleDraft } from './ArticlePane';
import { RichText } from '../../components/RichText';
import { sectionAnchor } from './html';
import { SectionEditor } from './SectionEditor';
import {
  moveSection,
  readableSections,
  renumberSections,
  templateSections,
  type KbSection,
} from './sections';

interface ArticleBodyProps {
  draft: ArticleDraft;
  onPatch: (teil: Partial<ArticleDraft>) => void;
  editing: boolean;
  bodyFallbackHeading: string;
}

/**
 * Der Text eines Artikels -- gelesen und geschrieben.
 *
 * **Beim Lesen stehen alle Abschnitte offen.** Vorher war nur der erste
 * aufgeklappt. Das spart Platz auf Kosten dessen, wofuer der Artikel da ist:
 * Wer eine Anleitung waehrend eines Telefonats abarbeitet, braucht sie
 * vollstaendig vor sich -- jeder Klick ist eine Hand, die vom Hoerer muss.
 * Wer nur zu "Durchfuehrung" will, nimmt das Inhaltsverzeichnis darueber;
 * das ist ein Klick statt fuenf.
 *
 * Die Klappen bleiben: F4-26 verlangt sie, und was man abgearbeitet hat, darf
 * aus dem Weg. Was sich geaendert hat, ist der Zustand, in dem der Artikel
 * aufgeht.
 *
 * Das Verzeichnis erscheint ab drei gefuellten Abschnitten. Bei zweien waere
 * es eine Zeile, die auf die Zeile darunter zeigt.
 */
export function ArticleBody({ draft, onPatch, editing, bodyFallbackHeading }: ArticleBodyProps) {
  const { t } = useTranslation();

  if (!editing) {
    const sichtbar = readableSections(draft.sections, draft.body, bodyFallbackHeading);

    if (sichtbar.length === 0) {
      return (
        <EmptyState
          icon="knowledge"
          title={t('knowledge.noText')}
          hint={t('knowledge.noTextHint')}
        />
      );
    }

    return (
      <div>
        {sichtbar.length >= 3 ? (
          <nav className="kb-toc" aria-label={t('knowledge.toc')}>
            {sichtbar.map((section, index) => (
              <a key={index} className="kb-toc__link" href={`#${sectionAnchor(index)}`}>
                {section.heading}
              </a>
            ))}
          </nav>
        ) : null}

        {/* Alle Abschnitte stehen offen -- ``openInitially`` bekommt ihre
            Zahl. Zuklappen bleibt moeglich (F4-26 verlangt das Akkordeon),
            aber der Vorgabezustand ist der, in dem die Anleitung
            vollstaendig dasteht. */}
        <Accordion
          openInitially={sichtbar.length}
          items={sichtbar.map((section, index) => ({
            id: String(index),
            anchor: sectionAnchor(index),
            heading: section.heading,
            content: (
              // Das HTML kommt aus ``kb_article_versions`` und ist beim
              // Speichern durch ``core/html.sanitise_html`` gelaufen. Die
              // Entschaerfung gehoert auf den Server: Eine im Browser waere
              // umgangen, sobald jemand die API unmittelbar anspricht.
              //
              // ``RichText`` steht **daneben** und nicht dafuer: Es setzt
              // das HTML nicht ein, sondern baut Elemente aus dem, was auf
              // der Liste steht. Der Altbestand aus der Zeit vor einer
              // Regelverschaerfung laeuft so durch die neue Regel.
              <RichText html={section.body_html} />
            ),
          }))}
        />
      </div>
    );
  }

  // Ein Artikel aus dem Altbestand: ein Feld wie bisher. Ihn beim Oeffnen
  // stillschweigend zu gliedern hiesse, seinen Text zu zerschneiden, ohne
  // dass jemand hingesehen hat.
  if (draft.sections === null) {
    return (
      <div className="kb-editor">
        <div className="field">
          <label className="field__label" htmlFor="kb-body">
            {t('knowledge.body')}
          </label>
          <textarea
            id="kb-body"
            className="field__control field__control--area"
            rows={14}
            value={draft.body}
            onChange={(event) => {
              onPatch({ body: event.target.value });
            }}
          />
          <span className="field__hint">{t('knowledge.bodyHint')}</span>
        </div>
        <div className="row">
          <Button
            onClick={() => {
              onPatch({ sections: templateSections() });
            }}
          >
            {t('knowledge.applyTemplate')}
          </Button>
          <span className="field__hint">{t('knowledge.applyTemplateHint')}</span>
        </div>
      </div>
    );
  }

  const sections = draft.sections;
  const setSections = (next: KbSection[]) => {
    onPatch({ sections: next });
  };

  return (
    <div className="kb-editor">
      <span className="field__hint">{t('knowledge.sectionsHint')}</span>

      {sections.map((section, index) => (
        <SectionEditor
          key={`${String(index)}-${section.heading}`}
          section={section}
          index={index}
          total={sections.length}
          onChange={(next) => {
            setSections(sections.map((item, position) => (position === index ? next : item)));
          }}
          onMove={(direction) => {
            setSections(moveSection(sections, index, direction));
          }}
          onRemove={() => {
            setSections(renumberSections(sections.filter((_, position) => position !== index)));
          }}
        />
      ))}

      <div className="row">
        <Button
          onClick={() => {
            setSections(
              renumberSections([
                ...sections,
                { heading: t('knowledge.newSection'), body_html: '', sort_order: 0 },
              ]),
            );
          }}
        >
          {t('knowledge.addSection')}
        </Button>
      </div>
    </div>
  );
}
